import React, { useMemo } from 'react';
import { Beaker, Droplets, Activity, Waves } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useAnalytics } from '../../hooks/useAnalytics';
import { InfoTooltipButton } from './InfoTooltipButton';
import './SoilSensorsTab.css';

// Statistics calculation helper
function calcStats(data, field) {
  const values = data.map(d => d[field]).filter(v => v != null && !isNaN(v));
  if (values.length === 0) return { mean: 0, min: 0, max: 0, stdDev: 0, n: 0 };
  
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);
  
  return { mean, min, max, stdDev, n };
}

// Custom Tooltips
function SoilMoistureTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  return (
    <div className="scientific-tooltip">
      <p className="scientific-tooltip__time">{format(parseISO(label), 'MMM dd, yyyy HH:mm')}</p>
      <table className="scientific-tooltip__table">
        <thead>
          <tr>
            <th>{t('analytics.soilSensors.depth')}</th>
            <th>{t('analytics.labels.field')}</th>
            <th>{t('analytics.labels.underPanel')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>10cm</td>
            <td>{payload.find(p => p.dataKey === 'campo_10cm')?.value?.toFixed(1)}%</td>
            <td>{payload.find(p => p.dataKey === 'retro_10cm')?.value?.toFixed(1)}%</td>
          </tr>
          <tr>
            <td>30cm</td>
            <td>{payload.find(p => p.dataKey === 'campo_30cm')?.value?.toFixed(1)}%</td>
            <td>{payload.find(p => p.dataKey === 'retro_30cm')?.value?.toFixed(1)}%</td>
          </tr>
          {payload.find(p => p.dataKey === 'campo_50cm' || p.dataKey === 'retro_50cm') && (
            <tr>
              <td>50cm</td>
              <td>{payload.find(p => p.dataKey === 'campo_50cm')?.value?.toFixed(1) || t('common.notAvailable')}%</td>
              <td>{payload.find(p => p.dataKey === 'retro_50cm')?.value?.toFixed(1) || t('common.notAvailable')}%</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PHTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  const campoPh = payload.find(p => p.dataKey === 'campo_ph')?.value || 0;
  const retroPh = payload.find(p => p.dataKey === 'retro_ph')?.value || 0;
  
  return (
    <div className="scientific-tooltip">
      <p className="scientific-tooltip__time">{format(parseISO(label), 'MMM dd, yyyy HH:mm')}</p>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#4A7A8A' }}>{t('analytics.labels.field')}:</span>
        <span>pH {campoPh.toFixed(2)}</span>
      </div>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#5A7A3A' }}>{t('analytics.labels.underPanel')}:</span>
        <span>pH {retroPh.toFixed(2)}</span>
      </div>
    </div>
  );
}

function ECTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  const campoEc = payload.find(p => p.dataKey === 'campo_ec')?.value || 0;
  const retroEc = payload.find(p => p.dataKey === 'retro_ec')?.value || 0;
  
  return (
    <div className="scientific-tooltip">
      <p className="scientific-tooltip__time">{format(parseISO(label), 'MMM dd, yyyy HH:mm')}</p>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#4A7A8A' }}>{t('analytics.labels.field')}:</span>
        <span>{campoEc.toFixed(2)} dS/m</span>
      </div>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#5A7A3A' }}>{t('analytics.labels.underPanel')}:</span>
        <span>{retroEc.toFixed(2)} dS/m</span>
      </div>
    </div>
  );
}

export function SoilSensorsTab() {
  const { hourlyData, dailyData, timeRange } = useAnalytics();
  const { t } = useTranslation();

  // Calculate statistics for soil sensors
  const soilStats = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return null;

    return {
      moisture10cm: {
        campo: calcStats(campo, 'soil_vwc_pct_10cm'),
        retro: calcStats(retro, 'soil_vwc_pct_10cm'),
      },
      moisture30cm: {
        campo: calcStats(campo, 'soil_vwc_pct_30cm'),
        retro: calcStats(retro, 'soil_vwc_pct_30cm'),
      },
      moisture50cm: {
        campo: calcStats(campo, 'soil_vwc_pct_50cm'),
        retro: calcStats(retro, 'soil_vwc_pct_50cm'),
      },
      ph: {
        campo: calcStats(campo, 'soil_ph'),
        retro: calcStats(retro, 'soil_ph'),
      },
      ec: {
        campo: calcStats(campo, 'soil_ec_dsm'),
        retro: calcStats(retro, 'soil_ec_dsm'),
      },
    };
  }, [hourlyData]);

  // Prepare soil moisture chart data
  const soilMoistureChartData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];

    // Sample data for performance (every 4th point for daily view)
    const step = Math.max(1, Math.floor(campo.length / 200));
    
    return campo.filter((_, i) => i % step === 0).map((c, idx) => {
      const r = retro[idx * step] || {};
      return {
        time: c.ts_iso,
        campo_10cm: c.soil_vwc_pct_10cm || 0,
        retro_10cm: r.soil_vwc_pct_10cm || 0,
        campo_30cm: c.soil_vwc_pct_30cm || 0,
        retro_30cm: r.soil_vwc_pct_30cm || 0,
        campo_50cm: c.soil_vwc_pct_50cm || 0,
        retro_50cm: r.soil_vwc_pct_50cm || 0,
      };
    });
  }, [hourlyData]);

  // Prepare pH chart data
  const phChartData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];

    const step = Math.max(1, Math.floor(campo.length / 150));
    
    return campo.filter((_, i) => i % step === 0).map((c, idx) => {
      const r = retro[idx * step] || {};
      return {
        time: c.ts_iso,
        campo_ph: c.soil_ph || 0,
        retro_ph: r.soil_ph || 0,
      };
    });
  }, [hourlyData]);

  // Prepare EC chart data
  const ecChartData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];

    const step = Math.max(1, Math.floor(campo.length / 150));
    
    return campo.filter((_, i) => i % step === 0).map((c, idx) => {
      const r = retro[idx * step] || {};
      return {
        time: c.ts_iso,
        campo_ec: c.soil_ec_dsm || 0,
        retro_ec: r.soil_ec_dsm || 0,
      };
    });
  }, [hourlyData]);

  // X-axis formatter based on time range
  const getXAxisFormat = () => {
    const days = timeRange?.days || 14;
    if (days <= 1) return 'HH:mm';
    if (days <= 7) return 'EEE HH:mm';
    return 'MMM dd';
  };

  const xAxisFormat = getXAxisFormat();

  return (
    <div className="soil-sensors-tab">
      {/* Soil Moisture Chart */}
      <section className="soil-sensors-tab__section">
        <div className="soil-sensors-tab__section-header">
          <div className="soil-sensors-tab__section-title-group">
            <Droplets size={20} className="soil-sensors-tab__section-icon" />
            <h3 className="soil-sensors-tab__section-title">{t('analytics.soilSensors.moisture.title')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.soilSensors.moisture.tooltip')} />
        </div>
        
        <div className="scientific-chart">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={soilMoistureChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="time"
                tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
              />
              <YAxis
                domain={[10, 55]}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
                label={{
                  value: t('analytics.soilSensors.axes.vwcPercent'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6B6B6B' }
                }}
              />
              <Tooltip content={<SoilMoistureTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              {/* Reference lines for field capacity and wilting point */}
              <ReferenceLine y={35} stroke="#5A7A3A" strokeDasharray="5 5" label={{ value: t('analytics.soilSensors.moisture.fieldCapacity'), position: 'insideTopRight', fontSize: 10 }} />
              <ReferenceLine y={15} stroke="#8A4A3A" strokeDasharray="5 5" label={{ value: t('analytics.soilSensors.moisture.wiltingPoint'), position: 'insideBottomRight', fontSize: 10 }} />
              
              <Line type="monotone" dataKey="retro_10cm" name={`${t('analytics.labels.underPanel')} 10cm`} stroke="#5A7A3A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="campo_10cm" name={`${t('analytics.labels.field')} 10cm`} stroke="#4A7A8A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="retro_30cm" name={`${t('analytics.labels.underPanel')} 30cm`} stroke="#7A9A5A" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="campo_30cm" name={`${t('analytics.labels.field')} 30cm`} stroke="#6A9AAA" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="retro_50cm" name={`${t('analytics.labels.underPanel')} 50cm`} stroke="#9ABA6A" strokeWidth={1} strokeDasharray="3 3" dot={false} />
              <Line type="monotone" dataKey="campo_50cm" name={`${t('analytics.labels.field')} 50cm`} stroke="#8AAA9A" strokeWidth={1} strokeDasharray="3 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="scientific-chart__interpretation">
            <strong>{t('analytics.common.interpretationLabel')}:</strong>{' '}
            {t('analytics.soilSensors.moisture.interpretation')}
          </div>
        </div>
      </section>

      {/* pH Chart */}
      <section className="soil-sensors-tab__section">
        <div className="soil-sensors-tab__section-header">
          <div className="soil-sensors-tab__section-title-group">
            <Activity size={20} className="soil-sensors-tab__section-icon" />
            <h3 className="soil-sensors-tab__section-title">{t('analytics.soilSensors.ph.title')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.soilSensors.ph.tooltip')} />
        </div>
        
        <div className="scientific-chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={phChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="time"
                tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
              />
              <YAxis
                domain={[5.5, 8.5]}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
                label={{
                  value: 'pH',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6B6B6B' }
                }}
              />
              <Tooltip content={<PHTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              {/* pH reference zones */}
              <ReferenceLine y={7.0} stroke="#333" strokeDasharray="3 3" label={{ value: t('analytics.soilSensors.ph.neutral'), position: 'insideTopRight', fontSize: 10 }} />
              <ReferenceLine y={6.0} stroke="#8A7A4A" strokeDasharray="3 3" label={{ value: t('analytics.soilSensors.ph.acidic'), position: 'insideBottomRight', fontSize: 10 }} />
              
              <Line type="monotone" dataKey="retro_ph" name={t('stations.types.underpanel')} stroke="#5A7A3A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="campo_ph" name={t('stations.types.field')} stroke="#4A7A8A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="scientific-chart__interpretation">
            <strong>{t('analytics.soilSensors.ph.scaleLabel')}:</strong> {t('analytics.soilSensors.ph.scale')}
          </div>
        </div>
      </section>

      {/* EC (Conductivity) Chart */}
      <section className="soil-sensors-tab__section">
        <div className="soil-sensors-tab__section-header">
          <div className="soil-sensors-tab__section-title-group">
            <Waves size={20} className="soil-sensors-tab__section-icon" />
            <h3 className="soil-sensors-tab__section-title">{t('analytics.soilSensors.ec.title')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.soilSensors.ec.tooltip')} />
        </div>
        
        <div className="scientific-chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={ecChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="time"
                tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
              />
              <YAxis
                domain={[0, 3]}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
                label={{
                  value: t('analytics.soilSensors.axes.ecDsm'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6B6B6B' }
                }}
              />
              <Tooltip content={<ECTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              {/* EC threshold lines */}
              <ReferenceLine y={2.0} stroke="#8A4A3A" strokeDasharray="5 5" label={{ value: t('analytics.soilSensors.ec.salineThreshold'), position: 'insideTopRight', fontSize: 10 }} />
              
              <Line type="monotone" dataKey="retro_ec" name={t('stations.types.underpanel')} stroke="#5A7A3A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="campo_ec" name={t('stations.types.field')} stroke="#4A7A8A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="scientific-chart__interpretation">
            <strong>{t('analytics.soilSensors.ec.interpretationLabel')}:</strong> {t('analytics.soilSensors.ec.interpretation')}
          </div>
        </div>
      </section>

      {/* Statistics Summary Table */}
      <section className="soil-sensors-tab__section">
        <div className="soil-sensors-tab__section-header">
          <div className="soil-sensors-tab__section-title-group">
            <Beaker size={20} className="soil-sensors-tab__section-icon" />
            <h3 className="soil-sensors-tab__section-title">{t('analytics.statsTable.title')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.soilSensors.stats.tooltip')} />
        </div>
        
        {soilStats && (
          <div className="statistics-table-container">
            <table className="statistics-table">
              <thead>
                <tr>
                  <th>{t('analytics.statsTable.metric')}</th>
                  <th>{t('analytics.statsTable.fieldMeanSd')}</th>
                  <th>{t('analytics.statsTable.underMeanSd')}</th>
                  <th>{t('analytics.statsTable.deltaDifference')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('analytics.soilSensors.stats.moisture10cm')}</td>
                  <td>{soilStats.moisture10cm.campo.mean.toFixed(1)} ± {soilStats.moisture10cm.campo.stdDev.toFixed(1)}%</td>
                  <td>{soilStats.moisture10cm.retro.mean.toFixed(1)} ± {soilStats.moisture10cm.retro.stdDev.toFixed(1)}%</td>
                  <td className={soilStats.moisture10cm.retro.mean > soilStats.moisture10cm.campo.mean ? 'positive' : 'negative'}>
                    {soilStats.moisture10cm.retro.mean > soilStats.moisture10cm.campo.mean ? '+' : ''}{(soilStats.moisture10cm.retro.mean - soilStats.moisture10cm.campo.mean).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.soilSensors.stats.moisture30cm')}</td>
                  <td>{soilStats.moisture30cm.campo.mean.toFixed(1)} ± {soilStats.moisture30cm.campo.stdDev.toFixed(1)}%</td>
                  <td>{soilStats.moisture30cm.retro.mean.toFixed(1)} ± {soilStats.moisture30cm.retro.stdDev.toFixed(1)}%</td>
                  <td className={soilStats.moisture30cm.retro.mean > soilStats.moisture30cm.campo.mean ? 'positive' : 'negative'}>
                    {soilStats.moisture30cm.retro.mean > soilStats.moisture30cm.campo.mean ? '+' : ''}{(soilStats.moisture30cm.retro.mean - soilStats.moisture30cm.campo.mean).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.soilSensors.stats.moisture50cm')}</td>
                  <td>{soilStats.moisture50cm.campo.mean.toFixed(1)} ± {soilStats.moisture50cm.campo.stdDev.toFixed(1)}%</td>
                  <td>{soilStats.moisture50cm.retro.mean.toFixed(1)} ± {soilStats.moisture50cm.retro.stdDev.toFixed(1)}%</td>
                  <td className={soilStats.moisture50cm.retro.mean > soilStats.moisture50cm.campo.mean ? 'positive' : 'negative'}>
                    {soilStats.moisture50cm.retro.mean > soilStats.moisture50cm.campo.mean ? '+' : ''}{(soilStats.moisture50cm.retro.mean - soilStats.moisture50cm.campo.mean).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.soilSensors.stats.ph')}</td>
                  <td>{soilStats.ph.campo.mean.toFixed(2)} ± {soilStats.ph.campo.stdDev.toFixed(2)}</td>
                  <td>{soilStats.ph.retro.mean.toFixed(2)} ± {soilStats.ph.retro.stdDev.toFixed(2)}</td>
                  <td>{(soilStats.ph.retro.mean - soilStats.ph.campo.mean) >= 0 ? '+' : ''}{(soilStats.ph.retro.mean - soilStats.ph.campo.mean).toFixed(2)}</td>
                </tr>
                <tr>
                  <td>{t('analytics.soilSensors.stats.ec')}</td>
                  <td>{soilStats.ec.campo.mean.toFixed(2)} ± {soilStats.ec.campo.stdDev.toFixed(2)}</td>
                  <td>{soilStats.ec.retro.mean.toFixed(2)} ± {soilStats.ec.retro.stdDev.toFixed(2)}</td>
                  <td>{(soilStats.ec.retro.mean - soilStats.ec.campo.mean) >= 0 ? '+' : ''}{(soilStats.ec.retro.mean - soilStats.ec.campo.mean).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            <p className="statistics-table__note">
              {t('analytics.statsTable.observationsPerStation', { count: soilStats.moisture10cm.campo.n })}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

